import type {
	AgentHealth,
	AgentKind,
	AgentRole,
	AgentStatus,
} from "@/common/model";
import { createStatusBadge, type TagStatus } from "../status-pill";

export const AgentRoleColorMap: Record<AgentRole, TagStatus> = {
	LEAD_AGENT: "processing",
	WORKER: "default",
};

export const AgentRoleLabelMap: Record<AgentRole, string> = {
	LEAD_AGENT: "Lead Agent",
	WORKER: "Worker",
};

export const AgentRoleBadge = createStatusBadge(
	AgentRoleColorMap,
	AgentRoleLabelMap,
);

const AgentKindColorMap: Record<AgentKind, TagStatus> = {
	MANAGED: "default",
	EXTERNAL: "processing",
};

export const AgentKindLabelMap: Record<AgentKind, string> = {
	MANAGED: "Managed",
	EXTERNAL: "External",
};

/**
 * How the platform relates to the agent's runtime (ADR-050): Managed agents are deployed and
 * invoked by the platform; External agents run on untracked infrastructure and pull their work.
 */
export const AgentKindBadge = createStatusBadge(
	AgentKindColorMap,
	AgentKindLabelMap,
);

const AgentStatusColorMap: Record<AgentStatus, TagStatus> = {
	ENABLED: "success",
	DISABLED: "default",
};

export const AgentStatusLabelMap: Record<AgentStatus, string> = {
	ENABLED: "Enabled",
	DISABLED: "Disabled",
};

/**
 * Customer-controlled lifecycle state (Enabled/Disabled), set via updateAgent. Distinct from the
 * system-observed {@link AgentHealthBadge}.
 */
export const AgentStatusBadge = createStatusBadge(
	AgentStatusColorMap,
	AgentStatusLabelMap,
);

const AgentHealthColorMap: Record<AgentHealth, TagStatus> = {
	READY: "success",
	PROVISIONING: "processing",
	UNREACHABLE: "warning",
	FAILED: "error",
};

const AgentHealthLabelMap: Record<AgentHealth, string> = {
	READY: "Ready",
	PROVISIONING: "Provisioning",
	UNREACHABLE: "Unreachable",
	FAILED: "Failed",
};

/**
 * System-observed reachability of the agent's runtime (read-only — refreshed by health pings). For
 * the customer-controlled enable/disable state see {@link AgentStatusBadge}.
 */
export const AgentHealthBadge = createStatusBadge(
	AgentHealthColorMap,
	AgentHealthLabelMap,
	{
		pulseWhen: ["PROVISIONING"],
	},
);
